<template>
  <section
    v-if="visibleTours.length"
    class="tours-wrap"
  >
    <div class="wrap">
      <SectionTitle
        :title="toursTitle"
        :show-more="t('city_tours_section.show_more')"
        :show-more-button="isShowMoreToursLinkShown"
        :href="bannerLink"
      />

      <div
        ref="toursInner"
        class="tours"
      >
        <div class="tours-inner">
          <CityTour
            v-for="exp in visibleTours"
            :key="exp.id"
            :exp="exp"
            :default-sogu-fields="defaultSoguFields"
            :closest-tour-label-key="closestTourLabelKey"
          />
        </div>
      </div>
      <div
        v-if="isShowMoreToursLinkShown"
        class="btn-wrap btn-wrap_more"
      >
        <ui-button
          data-test="listing-tours-bnt-show-all"
          :text="t('city_tours_section.show_more')"
          :variant="UiButtonVariant.OUTLINE"
          :color-mode="UiButtonColorMode.NEUTRAL"
          :href="bannerLink"
          :full-size="isMobile"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
  import ru from '../i18n/ru.json';
  import { useI18n } from '@experience/i18n';
  import { useCityTours } from '@/entities/cityTour/api/useCityTours.ts';
  import { useGetExperiences } from '@/entities/experience/list';
  import { UiButton, UiButtonColorMode, UiButtonVariant } from '@experience/core-button-ui';
  import { useScreenSize } from '@experience/shared';
  import { CityTour } from '@/entities/cityTour';
  import type { CityInfo } from '@/shared/api/experience';
  import { withMfsDomain } from '@/shared/lib/withMfsDomain';
  import { SectionTitle } from '@/entities/sectionTitle';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';

  const { isMobile } = useScreenSize();

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    slug: string;
    geoObject: CityInfo;
    defaultSoguFields?: ContextGeoSubject;
  }

  const props = defineProps<Props>();

  const { cityTours } = useCityTours(props.slug);

  const { experiencesData } = useGetExperiences(
    computed(() => ({
      price_format: 'travelers',
      region__slug: cityTours.value?.tours?.slug,
      offset: 0,
      bookable: 'all',
      persons_count: 1,
      type: 'tour',
    })),
    computed(() => !!cityTours.value?.tours?.slug),
  );

  const closestTourLabelKey = computed(() =>
    props.geoObject.country?.slug === 'russia' ? 'city_tour.closest_dates' : 'city_tour.closest_tour',
  );

  const toursTitle = computed(() => {
    return props.geoObject.country?.slug === 'russia'
      ? `${t('city_tours_section.mday_tours_in_region', { toObjPhrase: cityTours.value?.tours?.to_obj_phrase })}`
      : `${t('city_tours_section.tours_in_region', { toObjPhrase: cityTours.value?.tours?.to_obj_phrase })}`;
  });
  const isShowMoreToursLinkShown = computed(
    () => !!(experiencesData.value?.results?.length && experiencesData.value?.results?.length > 4),
  );

  const bannerLink = computed(() => {
    const slug = cityTours.value?.tours?.slug;
    const country = props.geoObject?.country?.slug;
    const url = `/tours/${country}/${slug}`;

    return withMfsDomain(url);
  });

  const visibleTours = computed(() => experiencesData.value?.results?.slice(0, 4) || []);
</script>
<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .tours-wrap {
    position: relative;
    padding: 80px 0;

    @include adaptivity.mobile {
      padding: 56px 0;
    }
  }

  .tours {
    display: grid;

    @include adaptivity.tablet {
      overflow-x: scroll;
      margin: 0 -20px;
      padding: 0 20px;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .tours-inner {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;

    @include adaptivity.tablet {
      grid-template-columns: repeat(4, 287px);
    }

    @include adaptivity.mobile {
      grid-template-columns: repeat(4, 280px);
      gap: 8px;
    }
  }

  .fake-anchor {
    position: absolute;
    top: -58px;
  }

  .btn-wrap_more {
    display: none;
    justify-content: center;
    width: 100%;
    margin-top: 16px;

    @include adaptivity.tablet {
      display: flex;
    }
  }
</style>
